/**
 * admin-svc /admin/compliance-docs — per-licensee compliance documents (V062).
 *
 * TREC's IABS is a per-LICENSEE disclosure: the brokerage has one, and each
 * agent uploads their own. Same two-legged presigned upload shape as the
 * brokerage logo (see brokerageSettings.ts) — presign → PUT straight to S3 →
 * confirm registers the key.
 */

import type { ApiClient } from "../client";

export interface ComplianceDoc {
  kind: string;
  agent_id: string | null;
  s3_key: string;
  uploaded_at: string;
}

export interface ComplianceDocUploadUrlResponse {
  upload_url: string;
  s3_key: string;
}

/** Omit agentId/userId for the brokerage's own document; an agent caller may
 * only ever target their own regardless of what's passed. `tenantId` lets a
 * super_admin target another tenant's brokerage-level document (TenantDetail
 * completion flow) — omit it to mean "my own tenant". */
export interface ComplianceDocTarget {
  agentId?: string;
  userId?: string;
  tenantId?: string;
}

function query(target?: ComplianceDocTarget): string {
  const params = new URLSearchParams();
  if (target?.agentId) params.set("agent_id", target.agentId);
  if (target?.userId) params.set("user_id", target.userId);
  if (target?.tenantId) params.set("tenant_id", target.tenantId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const complianceDocsService = {
  list(client: ApiClient, target?: ComplianceDocTarget) {
    return client.request<ComplianceDoc[]>(
      `/admin/compliance-docs${query(target)}`,
    );
  },

  presignUpload(client: ApiClient, kind: string, target?: ComplianceDocTarget) {
    return client.request<ComplianceDocUploadUrlResponse>(
      `/admin/compliance-docs/presign${query(target)}`,
      {
        method: "POST",
        body: { kind, agent_id: target?.agentId, user_id: target?.userId },
      },
    );
  },

  confirmUpload(
    client: ApiClient,
    s3Key: string,
    kind: string,
    target?: ComplianceDocTarget,
  ) {
    return client.request<ComplianceDoc>(
      `/admin/compliance-docs/confirm${query(target)}`,
      {
        method: "POST",
        body: {
          s3_key: s3Key,
          kind,
          agent_id: target?.agentId,
          user_id: target?.userId,
        },
      },
    );
  },
};

/** PUT the PDF straight to S3 — always application/pdf for compliance docs. */
export async function uploadComplianceDocFile(
  uploadUrl: string,
  file: Blob,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/pdf" },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Compliance doc upload failed (${response.status})`);
  }
}
